import { createReducer, INITIAL_STATE } from '@shared/redux';
import { LOAD_STATE } from '@shared/axios';
import { uuid4 } from '@shared/utils/uuid';
import { createMobeesAdApiConfig, updateMobeesAdApiConfig, deleteMobeesAdApiConfig } from './ad.api';
import { duplicateNewAd, formatAd, formatLoadAds } from './ad.utils';
import { AdState } from './ad.types';

export const CREATE_OR_UPDATE_AD_REQUEST_CALLED = '@mobees/newCampaign/AD_REQUEST_CALLED';
export const CREATE_OR_UPDATE_AD_REQUEST_SUCCEEDED = '@mobees/newCampaign/AD_REQUEST_SUCCEEDED';
export const CREATE_OR_UPDATE_AD_REQUEST_FAILED = '@mobees/newCampaign/AD_REQUEST_FAILED';

export const DELETE_AD_REQUEST_CALLED = '@mobees/newCampaign/DELETE_AD_REQUEST_CALLED';
export const DELETE_AD_REQUEST_SUCCEEDED = '@mobees/newCampaign/DELETE_AD_REQUEST_SUCCEEDED';
export const DELETE_AD_REQUEST_FAILED = '@mobees/newCampaign/DELETE_AD_REQUEST_FAILED';

export const CREATE_AD = '@mobees/newCampaign/CREATE_AD';
export const DELETE_AD = '@mobees/newCampaign/DELETE_AD';
export const DUPLICATE_AD = '@mobees/newCampaign/DUPLICATE_AD';
export const UPDATE_AD = '@mobees/newCampaign/UPDATE_AD';
export const UPDATE_ADS = '@mobees/newCampaign/UPDATE_ADS';
export const EDIT_AD = '@mobees/newCampaign/EDIT_AD';
export const RESET_ADS = '@mobees/newCampaign/RESET_ADS';

export const LOAD_MOBEES_ADS = '@mobees/newCampaign/LOAD_MOBEES_ADS';

export const HANDLE_SUBMIT_AD = '@mobees/newCampaign/HANDLE_SUBMIT_AD';
export const HANDLE_DELETE_AD = '@mobees/newCampaign/HANDLE_DELETE_AD';

export const createMobeesAd = ({ data, referenceId }) => ({
    type: CREATE_OR_UPDATE_AD_REQUEST_CALLED,
    actionTypes: {
        success: CREATE_OR_UPDATE_AD_REQUEST_SUCCEEDED,
        fail: CREATE_OR_UPDATE_AD_REQUEST_FAILED,
    },
    config: createMobeesAdApiConfig(data),
    referenceId,
});

export const updateMobeesAd = ({ remoteId, data, referenceId }) => ({
    type: CREATE_OR_UPDATE_AD_REQUEST_CALLED,
    actionTypes: {
        success: CREATE_OR_UPDATE_AD_REQUEST_SUCCEEDED,
        fail: CREATE_OR_UPDATE_AD_REQUEST_FAILED,
    },
    config: updateMobeesAdApiConfig(remoteId, data),
    referenceId,
});

export const createAd = () => ({
    type: CREATE_AD,
});

export const handleDeleteAd = id => ({
    type: HANDLE_DELETE_AD,
    payload: { id },
});

export const deleteAd = id => ({
    type: DELETE_AD,
    payload: { id },
});

export const deleteAdRequest = ({ referenceId, remoteId }) => ({
    type: DELETE_AD_REQUEST_CALLED,
    actionTypes: {
        success: DELETE_AD_REQUEST_SUCCEEDED,
        fail: DELETE_AD_REQUEST_FAILED,
    },
    config: deleteMobeesAdApiConfig(remoteId),
    referenceId,
});

export const loadAds = ads => ({
    type: LOAD_MOBEES_ADS,
    payload: {
        ads,
    },
});

export const duplicateAd = id => ({
    type: DUPLICATE_AD,
    payload: {
        id,
    },
});

export const updateAd = (data, id) => ({
    type: UPDATE_AD,
    payload: {
        id,
        data,
    },
});

export const updateAds = ads => ({
    type: UPDATE_ADS,
    payload: { ads },
});

export const editAd = id => ({
    type: EDIT_AD,
    payload: { id },
});

export const handleSubmitAd = id => ({
    type: HANDLE_SUBMIT_AD,
    payload: {
        id,
    },
});

export const resetAds = () => ({
    type: RESET_ADS,
    payload: {},
});

export const getAds = state => state.partnerships.mobees.newCampaign.ads.data;

const initialState: any = {
    data: [
        {
            id: uuid4(),
            remoteId: null,
            isCompleted: false,
            error: null,
            deleted: false,
            loadState: LOAD_STATE.SUCCESS,
            data: {},
        }],
};

export const mobeesAdsReducer = createReducer({
    [INITIAL_STATE]: initialState,
    [CREATE_OR_UPDATE_AD_REQUEST_CALLED]: (state: AdState, action) => ({
        ...state,
        data: [
            ...state.data.map(
                ad => (ad.id === action.referenceId ? {
                    ...ad, loadState: LOAD_STATE.LOADING, error: null,
                } : ad),
            ),
        ],
    }),
    [CREATE_OR_UPDATE_AD_REQUEST_SUCCEEDED]: (state: AdState, action) => ({
        ...state,
        data: formatAd(action.payload.data, state, action),
    }),
    [CREATE_OR_UPDATE_AD_REQUEST_FAILED]: (state: AdState, action) => ({
        ...state,
        data: [
            ...state.data.map(
                ad => (ad.id === action.requestAction.referenceId
                    ? { ...ad, loadState: LOAD_STATE.FAIL, error: action.payload } : ad),
            ),
        ],
    }),
    [CREATE_AD]: state => ({
        ...state,
        data: [...state.data, {
            id: uuid4(),
            isCompleted: false,
            data: {},
        }],
    }),
    [DELETE_AD_REQUEST_CALLED]: (state: AdState, action) => ({
        ...state,
        data: [
            ...state.data.map(
                ad => (ad.id === action.referenceId ? { ...ad, deleted: true } : ad),
            )],
    }),
    [DELETE_AD_REQUEST_FAILED]: (state: AdState, action) => ({
        ...state,
        data: [
            ...state.data.map(
                ad => (ad.id === action.requestAction.referenceId ? { ...ad, deleted: false } : ad),
            )],
    }),
    [DUPLICATE_AD]: (state: AdState, action) => ({
        ...state,
        data: [...state.data, duplicateNewAd(
            state.data.find(ad => ad.id === action.payload.id),
        )],
    }),
    [UPDATE_AD]: (state: AdState, action) => ({
        ...state,
        data: [
            ...state.data.map(
                ad => (
                    ad.id === action.payload.id
                        ? { ...ad, data: { ...ad.data, ...action.payload.data } }
                        : ad),
            )],
    }),
    [UPDATE_ADS]: (state: AdState, action) => ({
        ...state,
        data: [...action.payload.ads],
    }),
    [EDIT_AD]: (state: AdState, action) => ({
        ...state,
        data: [
            ...state.data.map(
                ad => (
                    ad.id === action.payload.id
                        ? { ...ad, isCompleted: false } : ad
                ),
            ),
        ],
    }),
    [DELETE_AD]: (state, action) => ({
        ...state,
        data: [...state.data.filter(ad => ad.id !== action.payload.id)],
    }),
    [LOAD_MOBEES_ADS]: (state, action) => ({
        ...state,
        data: formatLoadAds(action.payload.ads),
    }),
    [RESET_ADS]: () => initialState,
});
